<script setup>
import { ref, reactive, computed, watch, onMounted, onUnmounted, nextTick } from 'vue';
import { useToast } from 'vue-toastification';
import { useErrorLogger } from '@/composables/useErrorLogger';
import { useBarcodeLabel } from '@/composables/useBarcodeLabel';
import axios from 'axios';

const props = defineProps({
  token: { type: String, required: true },
  branchId: { type: String, required: true },
});

const toast = useToast();
const { logError } = useErrorLogger();
const { printProduct, isPrinting } = useBarcodeLabel();

const searchQuery = ref('');
const minCharacters = 5;
const page = ref(1);
const pageSize = ref(10);
const responseData = ref([]);
const expectedProductCount = ref(0);
const isRequsetBeingMade = ref(false);
const hasMoreData = ref(true);
// Tags from API can be a comma-separated string or an array
const getProductTags = (product) => {
  if (!product?.Tags) return [];
  if (Array.isArray(product.Tags)) return product.Tags.map(t => String(t).trim()).filter(Boolean);
  return String(product.Tags).split(',').map(t => t.trim()).filter(Boolean);
};

// ── Tag scroll strip ──────────────────────────────────────────────────────
const tagRefs = {};
const tagScrollStates = reactive({});

const setTagRef = (el, sku) => { if (el) tagRefs[sku] = el; };

const updateScrollState = (sku) => {
  const el = tagRefs[sku];
  if (!el) return;
  const canScrollLeft = el.scrollLeft > 2;
  const containerRight = el.getBoundingClientRect().right;
  let hiddenCount = 0;
  el.querySelectorAll('[data-tag]').forEach(t => {
    if (t.getBoundingClientRect().left >= containerRight - 2) hiddenCount++;
  });
  tagScrollStates[sku] = { canScrollLeft, hiddenCount };
};

const refreshAllTagStates = async () => {
  await nextTick();
  responseData.value.forEach(p => updateScrollState(p.Sku));
};

const scrollTagsRight = (sku) => {
  const el = tagRefs[sku];
  if (!el) return;
  el.scrollBy({ left: 160, behavior: 'smooth' });
  setTimeout(() => updateScrollState(sku), 350);
};

const scrollTagsLeft = (sku) => {
  const el = tagRefs[sku];
  if (!el) return;
  el.scrollBy({ left: -160, behavior: 'smooth' });
  setTimeout(() => updateScrollState(sku), 350);
};
// ─────────────────────────────────────────────────────────────────────────

const fetchProductByRetailSku = async (retailSku, isNextPage = false) => {
  if (isNextPage && !hasMoreData.value) return;
  if (isRequsetBeingMade.value) return;

  isRequsetBeingMade.value = true;

  try {
    const response = await axios.get(
      `/api/Order/GetProductByRetailSku?retailSku=${retailSku}&page=${page.value}&page_size=${pageSize.value}`,
      {
        headers: {
          'store-id': props.branchId,
          'Authorization': `Bearer ${props.token}`,
        }
      }
    );

    const products = response.data.Products || [];

    if (response.data.Status === 200 && products.length > 0) {
      expectedProductCount.value = products.length;
      if (isNextPage) {
        responseData.value = [...responseData.value, ...products];
      } else {
        responseData.value = products;
        hasMoreData.value = true;
      }
    } else {
      hasMoreData.value = false;
      toast.info('No more products');
    }
  } catch (error) {
    console.error('Error fetching product:', error);
    toast.error('Error fetching product');
    logError({ callStack: error?.stack || String(error), errorDetails: { message: error?.message, httpStatus: error?.response?.status, component: 'CheckInventoryContent', action: 'fetchProductByRetailSku', retailSku, branchId: props.branchId } });
  } finally {
    isRequsetBeingMade.value = false;
  }
};

const isSearchQueryOnlyNumbers = computed(() => /^\d+$/.test(searchQuery.value.trim()));

function handleEnterKey() {
  const query = searchQuery.value.trim();

  if (isSearchQueryOnlyNumbers.value) {
    if (query.length < 12 || query.length > 13) {
      toast.error('Barcode must 12 or 13 digits.');
      return;
    }
  } else {
    if (query.length < minCharacters) {
      toast.error(`Please enter at least ${minCharacters} characters to search the full catalog.`);
      return;
    }
  }

  searchQuery.value = query.replace(/\s+/g, '');
  page.value = 1;
  hasMoreData.value = true;
  refreshAllTagStates();
  fetchProductByRetailSku(searchQuery.value);
}

const loadNextPage = () => {
  page.value++;
  fetchProductByRetailSku(searchQuery.value, true);
};

function clearSearch() {
  searchQuery.value = '';
  responseData.value = [];
  refreshAllTagStates();
}

const groupedProducts = computed(() => {
  const groups = {};
  const bgColors = [
    'bg-yellow-100', 'bg-green-100', 'bg-blue-100', 'bg-purple-100',
    'bg-pink-100', 'bg-indigo-100', 'bg-red-100', 'bg-gray-200'
  ];
  let colorIndex = 0;

  for (const product of responseData.value) {
    const templateId = product.TemplateId;
    if (!groups[templateId]) {
      groups[templateId] = { templateId, products: [], bgColor: bgColors[colorIndex % bgColors.length] };
      colorIndex++;
    }
    product._bgColor = groups[templateId].bgColor;
    groups[templateId].products.push(product);
  }

  return Object.values(groups);
});

const flatProducts = computed(() => {
  const flat = [];
  const bgColors = [
    'bg-yellow-100', 'bg-green-100', 'bg-blue-100', 'bg-purple-100',
    'bg-pink-100', 'bg-indigo-100', 'bg-red-100', 'bg-gray-200'
  ];
  let colorIndex = 0;
  const templateTracker = new Set();

  for (const product of responseData.value) {
    const templateId = product.TemplateId;
    if (!templateTracker.has(templateId)) {
      templateTracker.add(templateId);
      product._bgColor = bgColors[colorIndex % bgColors.length];
      colorIndex++;
    } else {
      const existing = flat.find(p => p.TemplateId === templateId);
      product._bgColor = existing?._bgColor || 'bg-gray-100';
    }
    flat.push(product);
  }

  return flat;
});

const customBranchOrder = ['WHS', 'DTN', 'GOR', 'EDM', 'VIC', 'CLI', 'QEN', 'HUB', 'BER', 'OSH', 'TEX'];

const sortBranches = (branches = []) => {
  const filtered = branches.filter(branch => customBranchOrder.includes(branch.DisplayName));
  const uniqueBranches = [];
  const seenDisplayNames = new Set();
  for (const branch of filtered) {
    if (!seenDisplayNames.has(branch.DisplayName)) {
      seenDisplayNames.add(branch.DisplayName);
      uniqueBranches.push(branch);
    }
  }
  return uniqueBranches.sort((a, b) =>
    customBranchOrder.indexOf(a.DisplayName) - customBranchOrder.indexOf(b.DisplayName)
  );
};

const selectedSku = ref(null);
const copyToClipboard = (text, label) => {
  navigator.clipboard.writeText(text).then(() => {
    toast.success(`${label} copied!`);
  }).catch(() => {
    toast.error(`Failed to copy ${label}`);
  });
};

const printBarcode = async (product, count = 1) => {
  for (let i = 0; i < count; i++) {
    await printProduct(product);
    if (i < count - 1) await new Promise(r => setTimeout(r, 120));
  }
};

const printTarget = ref(null);
let printCountInputEl = null;

function onPrintClick(product) {
  printBarcode(product, 1);
}

async function onPrintMultipleClick(product) {
  printTarget.value = { product, count: 3 };
  await nextTick();
  printCountInputEl?.focus();
  printCountInputEl?.select();
}

async function confirmPrintWithCount() {
  if (!printTarget.value) return;
  const { product, count } = printTarget.value;
  printTarget.value = null;
  await printBarcode(product, Math.max(1, count));
}

const hasAnyValidCosts = computed(() =>
  responseData.value.some(product => {
    const cost = product.Cost;
    return cost && cost !== '-' && !isNaN(parseFloat(cost));
  })
);

const getGorillaPrice = (product) => product.Branches?.find(b => b.PriceList === 'RET - Gorilla')?.Price ?? null;
const getCamoPrice   = (product) => product.Branches?.find(b => b.PriceList === 'RET - Camo')?.Price ?? null;
const getTexasPrice  = (product) => product.Branches?.find(b => b.PriceList === 'RET - CamoUSA')?.Price ?? null;

const viewMode = ref('list');
const cardContainerRef = ref(null);
const listContainerRef = ref(null);
const getActiveContainer = () => viewMode.value === 'card' ? cardContainerRef.value : listContainerRef.value;

const handleScroll = () => {
  if (!hasMoreData.value || isRequsetBeingMade.value) return;
  const el = getActiveContainer();
  if (!el) return;
  if (el.scrollTop + el.clientHeight >= el.scrollHeight - 100) loadNextPage();
};

const setupScrollListener = async () => {
  await nextTick();
  const el = getActiveContainer();
  if (!el) return;
  el.removeEventListener('scroll', handleScroll);
  el.addEventListener('scroll', handleScroll);
};

onMounted(() => { setupScrollListener(); refreshAllTagStates(); });
watch(viewMode, () => { setupScrollListener(); refreshAllTagStates(); });
onUnmounted(() => {
  const cardEl = cardContainerRef.value;
  const listEl = listContainerRef.value;
  if (cardEl) cardEl.removeEventListener('scroll', handleScroll);
  if (listEl) listEl.removeEventListener('scroll', handleScroll);
});
watch(responseData, async (newVal) => {
  if (newVal.length > 0) {
    await nextTick();
    setupScrollListener();
    refreshAllTagStates();
  }
});

// Parse "3 (ETA: 03-May-26)" → { qty: '3', eta: '03-May-26' }
const parseOnOrderQty = (raw) => {
  if (!raw) return null;
  const m = raw.match(/^(.+?)\s*\(ETA:\s*(.+?)\)$/);
  return m ? { qty: m[1].trim(), eta: m[2].trim() } : { qty: raw.trim(), eta: null };
};

const formatReconciliationDate = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return new Intl.DateTimeFormat('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
    .format(date).replace(/ /g, '-');
};

const borderColorMap = {
  A: 'border-pink-400', B: 'border-green-400', C: 'border-blue-400', D: 'border-yellow-400',
  E: 'border-purple-400', F: 'border-red-400', G: 'border-indigo-400', H: 'border-teal-400',
  I: 'border-cyan-400', J: 'border-lime-400', K: 'border-amber-400', L: 'border-orange-400',
  M: 'border-fuchsia-400', N: 'border-rose-400', O: 'border-sky-400', P: 'border-violet-400',
  Q: 'border-emerald-400', R: 'border-yellow-600', S: 'border-blue-600', T: 'border-purple-600',
  U: 'border-pink-600', V: 'border-green-600', W: 'border-orange-600', X: 'border-red-600',
  Y: 'border-teal-600', Z: 'border-indigo-600'
};

const getItemClassBorder = (itemClass) => {
  if (!itemClass || itemClass.length === 0) return 'border-gray-300';
  return borderColorMap[itemClass.charAt(0).toUpperCase()] || 'border-gray-300';
};
</script>

<template>
  <div class="relative w-full">
    <input
      ref="searchInput"
      v-model="searchQuery"
      type="text"
      autocomplete="off"
      name="barcode-search"
      id="barcode-search"
      class="bg-gray-100 border-0 outline-none p-2 rounded-lg w-full pr-10"
      placeholder="Search by Retail Sku"
      @keydown.enter="handleEnterKey"
    />
    <button v-if="searchQuery" @click="clearSearch"
      class="absolute right-2 top-2 text-gray-500 hover:text-gray-700">
      <i class="pi pi-times pr-2"></i>
    </button>
  </div>

  <div class="flex items-center justify-between h-8 py-6">
    <div class="flex items-center gap-2">
      <p class="font-semibold text-lg">Order By Total Qty</p>
      <button><i class="pi pi-sort"></i></button>
      <i class="pi pi-sort-amount-down-alt"></i>
    </div>
    <div class="flex gap-2 items-center">
      <button
        class="px-3 py-1 rounded border text-sm"
        :class="viewMode === 'list' ? 'bg-purple-100 font-semibold' : 'bg-white'"
        @click="viewMode = 'list'"
      >
        <i class="pi pi-bars"></i>
      </button>
      <button
        class="px-3 py-1 rounded border text-sm"
        :class="viewMode === 'card' ? 'bg-purple-100 font-semibold' : 'bg-white'"
        @click="viewMode = 'card'"
      >
        <i class="pi pi-th-large"></i>
      </button>
    </div>
  </div>

  <div v-if="isRequsetBeingMade && !responseData.length">
    <p class="text-gray-500">Loading products ...</p>
  </div>

  <div v-else-if="responseData.length > 0" class="">
    <div class="gap-4">

      <!-- List View -->
      <div v-if="viewMode === 'list'">
        <div class="">
          <div class="overflow-y-auto overflow-x-hidden p-2" ref="listContainerRef" style="max-height: 670px;">
            <table class="w-full bg-white text-sm">
              <thead class="bg-gray-100 sticky -top-2 z-10">
                <tr>
                  <th class="text-left p-2 border-b bg-gray-200">Image</th>
                  <th class="text-left p-2 border-b bg-gray-200">Item Name</th>
                  <th class="text-left p-2 border-b bg-gray-200">RetailSku</th>
                  <th class="text-left p-2 border-b bg-gray-200">Barcode</th>
                  <th v-if="hasAnyValidCosts" class="text-left p-2 border-b bg-gray-200">Cost</th>
                  <th class="text-left p-2 border-b bg-gray-200">Gor Price</th>
                  <th class="text-left p-2 border-b bg-gray-200">Camo Price</th>
                  <th class="text-left p-2 border-b bg-gray-200">Location</th>
                  <th class="text-left p-2 border-b bg-gray-200">TemplateId</th>
                </tr>
              </thead>
              <tbody class="relative">
                <template v-for="(product, index) in flatProducts" :key="product.Sku">
                  <p class="absolute left-24 -translate-x-1/2 text-6xl font-bold opacity-5">{{ product.ItemClass[0] }}</p>
                  <tr :class="[index % 2 === 0 ? 'bg-white' : 'bg-gray-200', getItemClassBorder(product.ItemClass), 'border-l-8']">
                    <td class="px-2">
                      <img :src="product.ImageUrl" alt="Img" class="w-12 h-12 object-cover rounded-md" />
                    </td>
                    <td class="px-2" :title="product.ItemName">
                      <div class="">
                        {{ product.ItemName.slice(0, 22) }}<span v-if="product.ItemName.length > 28">...</span>
                      </div>
                      <span v-if="product.Discontinued" class="text-red-500 bg-red-200 px-1 rounded">Discontinued</span>
                      <div v-if="getProductTags(product).length" class="relative mt-1" style="height:20px;">
                        <div class="absolute inset-0 flex items-center gap-1">
                          <button
                            v-if="tagScrollStates[product.Sku]?.canScrollLeft"
                            @click.stop="scrollTagsLeft(product.Sku)"
                            class="flex-shrink-0 w-4 h-4 flex items-center justify-center rounded-full bg-gray-200 hover:bg-gray-300 text-gray-500 text-sm leading-none select-none"
                          >‹</button>
                          <div
                            :ref="el => setTagRef(el, product.Sku)"
                            class="tag-strip flex-1 min-w-0 flex flex-nowrap gap-1"
                            @scroll="updateScrollState(product.Sku)"
                          >
                            <span
                              v-for="tag in getProductTags(product)"
                              :key="tag"
                              data-tag
                              :title="tag"
                              class="text-xs px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700 font-medium whitespace-nowrap flex-shrink-0"
                            >{{ tag }}</span>
                          </div>
                          <button
                            v-if="tagScrollStates[product.Sku]?.hiddenCount > 0"
                            @click.stop="scrollTagsRight(product.Sku)"
                            class="flex-shrink-0 text-xs px-1.5 py-0.5 rounded-full bg-gray-200 text-gray-600 font-medium hover:bg-gray-300 select-none"
                          >+{{ tagScrollStates[product.Sku].hiddenCount }}</button>
                        </div>
                      </div>
                    </td>
                    <td class="px-2">{{ product.RetailSku }}</td>
                    <td class="px-2">{{ product.Barcodes[0]?.Barcode }}</td>
                    <td v-if="hasAnyValidCosts" class="px-2">{{ product.Cost && product.Cost !== '-' ? `$${parseFloat(product.Cost).toFixed(2)}` : '-' }}</td>
                    <td class="px-2">${{ getGorillaPrice(product) }}</td>
                    <td class="px-2">${{ getCamoPrice(product) }}</td>
                    <td class="px-2">{{ product.Location }}</td>
                    <td class="px-2 text-sm">
                      <span :class="[product._bgColor, 'px-2 py-1 rounded-full inline-block w-full']">
                        {{ product.TemplateId }}
                      </span>
                    </td>
                  </tr>

                  <tr :class="index % 2 === 0 ? 'bg-white' : 'bg-gray-200'">
                    <td colspan="12" class="px-2">
                      <div v-if="product.Branches?.length" class="w-full flex justify-end flex-wrap items-center gap-2 pb-2">
                        <div v-if="product.OnOrderQty" class="flex items-center gap-1.5 bg-yellow-400 rounded py-1 px-2">
                          <p>On Order: {{ parseOnOrderQty(product.OnOrderQty).qty }}</p>
                          <div v-if="parseOnOrderQty(product.OnOrderQty).eta" class="relative group">
                            <span class="text-xs bg-yellow-600 text-white px-1.5 py-0.5 rounded cursor-default font-semibold">ETA</span>
                            <div class="tooltip-tip">{{ parseOnOrderQty(product.OnOrderQty).eta }}</div>
                          </div>
                        </div>
                        <div v-for="(branch, bIndex) in sortBranches(product.Branches)" :key="branch.Branch + bIndex">
                          <div class="flex items-center gap-1 px-2 py-1 rounded font-medium"
                            :class="{
                              'bg-emerald-50 text-green-700': branch.Qty > 5,
                              'bg-cyan-50 text-cyan-700': branch.Qty > 0 && branch.Qty <= 5,
                              'bg-gray-50 text-gray-400': branch.Qty <= 0
                            }"
                            :title="branch?.LastReconciliation ? `Reconciled on ${formatReconciliationDate(branch.LastReconciliation)}` : 'Not reconciled'"
                          >
                            <p class="font-medium cursor-pointer text-xs">{{ branch.DisplayName }}:</p>
                            <p class="text-xs">{{ branch.Qty }}</p>
                          </div>
                        </div>

                        <div class="relative group">
                          <button class="flex items-center gap-2 text-xl text-purple-600 hover:text-gray-800"
                            @click.stop="copyToClipboard(product.Barcodes[0]?.Barcode, 'Barcode')">
                            <i class="pi pi-barcode" style="font-size: 16px;"></i><i class="pi pi-copy" style="font-size: 16px;"></i>
                          </button>
                          <div class="tooltip-tip tooltip-tip--left">Copy Barcode</div>
                        </div>

                        <div class="relative group">
                          <button class="flex items-center gap-1 text-xl text-purple-600 hover:text-gray-800"
                            @click.stop="copyToClipboard(product.Sku, 'ItemCode')">
                            <span class="text-sm">Sku</span><i class="pi pi-copy" style="font-size: 16px;"></i>
                          </button>
                          <div class="tooltip-tip tooltip-tip--left">Copy SKU</div>
                        </div>

                        <div class="relative group">
                          <button class="flex items-center gap-1 text-xl text-purple-600 hover:text-gray-800"
                            @click.stop="copyToClipboard(product.RetailSku, 'RetailSku')">
                            <span class="text-sm">RSku</span><i class="pi pi-copy" style="font-size: 16px;"></i>
                          </button>
                          <div class="tooltip-tip tooltip-tip--left">Copy Retail SKU</div>
                        </div>

                        <div class="relative group">
                          <button class="flex items-center gap-2 text-xl text-purple-600 hover:text-gray-800"
                            @click.stop="onPrintClick(product)">
                            <i class="pi pi-barcode" style="font-size: 16px;"></i><i class="pi pi-print" style="font-size: 16px;"></i>
                          </button>
                          <div class="tooltip-tip tooltip-tip--left">Print barcode</div>
                        </div>

                        <div class="relative group">
                          <button class="flex items-center gap-2 text-xl text-purple-600 hover:text-gray-800"
                            @click.stop="onPrintMultipleClick(product)">
                            <i class="pi pi-barcode" style="font-size: 16px;"></i><i class="pi pi-print" style="font-size: 16px;"></i><span class="text-xs font-semibold -ml-1">x</span>
                          </button>
                          <div class="tooltip-tip tooltip-tip--left">Print multiple barcodes</div>

                          <div v-if="printTarget?.product.Sku === product.Sku"
                            class="absolute bottom-full right-0 mb-2 flex items-center gap-1 bg-white border border-gray-200 rounded-lg shadow-lg p-2 z-20"
                            @click.stop>
                            <input
                              :ref="el => { printCountInputEl = el }"
                              v-model.number="printTarget.count"
                              type="number" min="1" max="99"
                              class="w-14 px-2 py-1 bg-gray-100 rounded text-center text-sm outline-none"
                              @keydown.enter="confirmPrintWithCount"
                              @keydown.esc="printTarget = null"
                            />
                            <button class="px-2 py-1 bg-purple-600 text-white text-xs rounded hover:bg-purple-700"
                              @click="confirmPrintWithCount">Print</button>
                            <button class="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded hover:bg-gray-200"
                              @click="printTarget = null">✕</button>
                          </div>
                        </div>
                      </div>
                    </td>
                  </tr>
                </template>

                <div v-if="isRequsetBeingMade && responseData.length > 0"
                  class="absolute bottom-0 h-10 w-full flex justify-center items-center py-4">
                  <span class="text-gray-500">Loading more products...</span>
                </div>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- Card View -->
      <div v-if="viewMode === 'card'">
        <div class="overflow-y-auto p-2" ref="cardContainerRef" style="max-height: 670px;">
          <div>
            <div v-for="group in groupedProducts" :key="group.templateId" class="w-full">
              <div class="grid grid-cols-3 gap-4 mb-4">
                <div v-for="product in group.products" :key="product.Sku" class="bg-gray-100 rounded-2xl">
                  <div @click="selectedSku = selectedSku === product.Sku ? null : product.Sku"
                    class="relative bg-gray-200 p-4 rounded-2xl cursor-pointer hover:ring-2 hover:ring-purple-300 transition">
                    <p class="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-8xl font-bold opacity-10">{{ product.ItemClass[0] }}</p>
                    <div class="flex gap-4 items-start relative">
                      <img :src="product.ImageUrl" alt="Image" class="w-24 h-24 object-cover rounded-lg" />
                      <div class="flex-1" :class="[getItemClassBorder(product.ItemClass), 'border-l-4 pl-2']">
                        <div>
                          <p class="font-semibold">{{ product.ItemName }}</p>
                          <span v-if="product.Discontinued" class="text-red-500 bg-red-200 px-1 rounded">Discontinued</span>
                        </div>
                        <p class="text-sm text-gray-500">RetailSku: {{ product.RetailSku }}</p>
                        <p class="text-sm text-gray-500">Barcode: {{ product.Barcodes[0]?.Barcode }}</p>
                        <p class="text-sm text-gray-500">Location: {{ product.Location }}</p>
                        <div v-if="getProductTags(product).length" class="relative mt-2" style="height:20px;">
                          <div class="absolute inset-0 flex items-center gap-1">
                            <button
                              v-if="tagScrollStates[product.Sku]?.canScrollLeft"
                              @click.stop="scrollTagsLeft(product.Sku)"
                              class="flex-shrink-0 w-4 h-4 flex items-center justify-center rounded-full bg-gray-200 hover:bg-gray-300 text-gray-500 text-sm leading-none select-none"
                            >‹</button>
                            <div
                              :ref="el => setTagRef(el, product.Sku)"
                              class="tag-strip flex-1 min-w-0 flex flex-nowrap gap-1"
                              @scroll="updateScrollState(product.Sku)"
                            >
                              <span
                                v-for="tag in getProductTags(product)"
                                :key="tag"
                                data-tag
                                :title="tag"
                                class="text-xs px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700 font-medium whitespace-nowrap flex-shrink-0"
                              >{{ tag }}</span>
                            </div>
                            <button
                              v-if="tagScrollStates[product.Sku]?.hiddenCount > 0"
                              @click.stop="scrollTagsRight(product.Sku)"
                              class="flex-shrink-0 text-xs px-1.5 py-0.5 rounded-full bg-gray-200 text-gray-600 font-medium hover:bg-gray-300 select-none"
                            >+{{ tagScrollStates[product.Sku].hiddenCount }}</button>
                          </div>
                        </div>
                        <div v-if="product.OnOrderQty" class="flex items-center gap-1.5 w-fit bg-yellow-400 rounded py-1 px-2">
                          <p>On Order: {{ parseOnOrderQty(product.OnOrderQty).qty }}</p>
                          <div v-if="parseOnOrderQty(product.OnOrderQty).eta" class="relative group">
                            <span class="text-xs bg-yellow-600 text-white px-1.5 py-0.5 rounded cursor-default font-semibold">ETA</span>
                            <div class="tooltip-tip">{{ parseOnOrderQty(product.OnOrderQty).eta }}</div>
                          </div>
                        </div>
                        <span :class="[product._bgColor, 'absolute right-2 bottom-2 px-2 py-1 text-xs font-medium rounded-full text-gray-800']">
                          {{ product.TemplateId }}
                        </span>
                      </div>
                    </div>

                    <div v-if="selectedSku === product.Sku"
                      class="absolute top-1/2 right-0 translate-x-full -translate-y-1/2 bg-white border rounded-lg shadow-md z-10 p-2 flex flex-col gap-2">

                      <div class="relative group">
                        <button class="flex items-center gap-2 text-xl text-purple-600 hover:text-gray-800"
                          @click.stop="copyToClipboard(product.Barcodes[0]?.Barcode, 'Barcode')">
                          <i class="pi pi-barcode" style="font-size: 16px;"></i><i class="pi pi-copy" style="font-size: 16px;"></i>
                        </button>
                        <div class="tooltip-tip tooltip-tip--left">Copy Barcode</div>
                      </div>

                      <div class="relative group">
                        <button class="flex items-center gap-1 text-xl text-purple-600 hover:text-gray-800"
                          @click.stop="copyToClipboard(product.Sku, 'ItemCode')">
                          <span class="text-sm">Sku</span><i class="pi pi-copy" style="font-size: 16px;"></i>
                        </button>
                        <div class="tooltip-tip tooltip-tip--left">Copy SKU</div>
                      </div>

                      <div class="relative group">
                        <button class="flex items-center gap-1 text-xl text-purple-600 hover:text-gray-800"
                          @click.stop="copyToClipboard(product.RetailSku, 'RetailSku')">
                          <span class="text-sm">RSku</span><i class="pi pi-copy" style="font-size: 16px;"></i>
                        </button>
                        <div class="tooltip-tip tooltip-tip--left">Copy Retail SKU</div>
                      </div>

                      <div class="relative group">
                        <button class="flex items-center gap-2 text-xl text-purple-600 hover:text-gray-800 select-none"
                          @click.stop="onPrintClick(product)">
                          <i class="pi pi-barcode" style="font-size: 16px;"></i><i class="pi pi-print" style="font-size: 16px;"></i>
                        </button>
                        <div class="tooltip-tip tooltip-tip--left">Print barcode</div>
                      </div>

                      <div class="relative group">
                        <button class="flex items-center gap-2 text-xl text-purple-600 hover:text-gray-800 select-none"
                          @click.stop="onPrintMultipleClick(product)">
                          <i class="pi pi-barcode" style="font-size: 16px;"></i><i class="pi pi-print" style="font-size: 16px;"></i><span class="text-xs font-semibold -ml-1">x#</span>
                        </button>
                        <div class="tooltip-tip tooltip-tip--left">Print multiple barcodes</div>

                        <div v-if="printTarget?.product.Sku === product.Sku"
                          class="absolute bottom-full right-0 mb-2 flex items-center gap-1 bg-white border border-gray-200 rounded-lg shadow-lg p-2 z-20"
                          @click.stop>
                          <input
                            :ref="el => { printCountInputEl = el }"
                            v-model.number="printTarget.count"
                            type="number" min="1" max="99"
                            class="w-14 px-2 py-1 bg-gray-100 rounded text-center text-sm outline-none"
                            @keydown.enter="confirmPrintWithCount"
                            @keydown.esc="printTarget = null"
                          />
                          <button class="px-2 py-1 bg-purple-600 text-white text-xs rounded hover:bg-purple-700"
                            @click="confirmPrintWithCount">Print</button>
                          <button class="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded hover:bg-gray-200"
                            @click="printTarget = null">✕</button>
                        </div>
                      </div>
                    </div>

                    <div class="flex justify-between bg-white rounded-lg my-2 p-3">
                      <p v-if="hasAnyValidCosts" class="text-sm"><strong>Cost:</strong> {{ product.Cost && product.Cost !== '-' ? `$${parseFloat(product.Cost).toFixed(2)}` : '-' }}</p>
                      <p class="text-sm"><strong>Gor Price:</strong> ${{ getGorillaPrice(product) }}</p>
                      <p class="text-sm"><strong>Camo Price:</strong> ${{ getCamoPrice(product) }}</p>
                    </div>

                    <div class="mt-3 bg-white rounded-lg p-3">
                      <div class="grid grid-cols-3 font-semibold pb-2 mb-2">
                        <div>Branches</div>
                        <div class="text-center w-[50%]">Qty</div>
                        <div>Reconciled on</div>
                      </div>
                      <div
                        v-for="(branch, bIndex) in sortBranches(product.Branches.filter(b =>
                          !['SS01-Hubert-01 Operators', 'ZE01-Bermondsey-01 Operators', 'ZE02-Oshawa-01 Operators', 'AO01-Houston Operators', 'Qc1 Operators', 'Montreal Operators'].includes(b.Branch)
                        ))"
                        :key="branch.Branch + bIndex"
                        class="grid grid-cols-3 py-2"
                      >
                        <div>{{ branch.DisplayName }}</div>
                        <div class="px-2 py-1 rounded w-[50%] text-center"
                          :class="{
                            'bg-emerald-50 text-green-700': branch.Qty > 5,
                            'bg-cyan-50 text-cyan-700': branch.Qty > 0 && branch.Qty <= 5,
                            'bg-gray-50 text-gray-400': branch.Qty <= 0
                          }">{{ branch.Qty }}</div>
                        <div>{{ formatReconciliationDate(branch.LastReconciliation) || 'Not reconciled' }}</div>
                      </div>
                    </div>

                    <div v-if="product.Branches.some(b => ['SS01-Hubert-01 Operators', 'ZE01-Bermondsey-01 Operators', 'ZE02-Oshawa-01 Operators'].includes(b.Branch))"
                      class="mt-2 p-3 bg-white rounded-lg">
                      <div class="bg-white rounded-lg">
                        <div
                          v-for="(branch, bIndex) in sortBranches(product.Branches.filter(b =>
                            ['SS01-Hubert-01 Operators', 'ZE01-Bermondsey-01 Operators', 'ZE02-Oshawa-01 Operators'].includes(b.Branch)
                          ))"
                          :key="branch.Branch + bIndex"
                          class="grid grid-cols-3 py-2"
                        >
                          <div>{{ branch.DisplayName }}</div>
                          <div class="px-2 py-1 rounded w-[50%] text-center"
                            :class="{
                              'bg-emerald-50 text-emerald-700': branch.Qty > 5,
                              'bg-cyan-50 text-cyan-700': branch.Qty > 0 && branch.Qty <= 5,
                              'bg-gray-50 text-gray-400': branch.Qty <= 0
                            }">{{ branch.Qty }}</div>
                          <div>{{ formatReconciliationDate(branch.LastReconciliation) || 'Not reconciled' }}</div>
                        </div>
                      </div>
                    </div>

                    <div v-if="product.Branches.some(b => b.Branch === 'AO01-Houston Operators')"
                      class="mt-2 p-3 bg-white rounded-lg">
                      <div class="bg-white rounded-lg">
                        <p class="text-sm"><strong>CamoUSD: </strong>{{ getTexasPrice(product) }}</p>
                        <div class="grid grid-cols-3 py-2">
                          <div>TEX</div>
                          <div class="px-2 py-1 rounded w-[50%] text-center"
                            :class="{
                              'bg-emerald-50 text-emerald-700': product.Branches.find(b => b.Branch === 'AO01-Houston Operators')?.Qty > 5,
                              'bg-cyan-50 text-cyan-700': product.Branches.find(b => b.Branch === 'AO01-Houston Operators')?.Qty > 0 && product.Branches.find(b => b.Branch === 'AO01-Houston Operators')?.Qty <= 5,
                              'bg-gray-50 text-gray-400': product.Branches.find(b => b.Branch === 'AO01-Houston Operators')?.Qty === 0
                            }">
                            {{ product.Branches.find(b => b.Branch === 'AO01-Houston Operators')?.Qty }}
                          </div>
                          <div>
                            {{ product.Branches.find(b => b.Branch === 'AO01-Houston Operators')?.LastReconciliation
                              ? formatReconciliationDate(product.Branches.find(b => b.Branch === 'AO01-Houston Operators')?.LastReconciliation)
                              : 'Not reconciled' }}
                          </div>
                        </div>
                      </div>
                    </div>

                  </div>
                </div>
              </div>
            </div>

            <div v-if="isRequsetBeingMade && responseData.length > 0"
              class="absolute bottom-0 h-10 w-full flex justify-center items-center py-4">
              <span class="text-gray-500">Loading more products...</span>
            </div>
          </div>
        </div>
      </div>

    </div>
  </div>
</template>

<style scoped>
.tooltip-tip {
  position: absolute;
  bottom: calc(100% + 6px);
  left: 50%;
  transform: translateX(-50%);
  background: #1f2937;
  color: #fff;
  font-size: 11px;
  white-space: nowrap;
  padding: 3px 8px;
  border-radius: 5px;
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.15s;
  z-index: 30;
}

.tooltip-tip--left {
  left: auto;
  right: 0;
  transform: none;
}

.group:hover .tooltip-tip {
  opacity: 1;
}

input[type="number"]::-webkit-inner-spin-button,
input[type="number"]::-webkit-outer-spin-button {
  -webkit-appearance: none;
}
input[type="number"] { -moz-appearance: textfield; }

.tag-strip {
  overflow-x: scroll;
  scrollbar-width: none;
}
.tag-strip::-webkit-scrollbar { display: none; }
</style>
